/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Lightweight controller that turns a static slide list into an Embla carousel
// only when a given media query matches (typically: mobile only). Above the
// breakpoint, the controller leaves the DOM untouched so the host can fall
// back to a CSS grid/flex layout.
//
// Inputs:
//   - viewport: HTMLElement Embla mounts on (must wrap `slides`)
//   - container: HTMLElement holding the slide list
//   - getSlides: () => HTMLElement[] (called every (re)init to support DOM changes)
//   - mediaQuery: string passed to window.matchMedia (default: pocket)
//   - options: Embla options
//   - onSelect: optional callback invoked with the active slide on every change
class ResponsiveEmbla {
  static DEFAULT_MEDIA_QUERY = '(max-width: 999px)';
  static DEFAULT_OPTIONS = { align: 'center', containScroll: 'trimSnaps', loop: false, dragFree: false };

  constructor({ viewport, container, getSlides, mediaQuery, options, onSelect }) {
    this.viewport = viewport;
    this.container = container;
    this.getSlides = getSlides;
    this.options = { ...ResponsiveEmbla.DEFAULT_OPTIONS, ...(options ?? {}) };
    this.onSelect = onSelect;
    this.mediaQuery = window.matchMedia(mediaQuery ?? ResponsiveEmbla.DEFAULT_MEDIA_QUERY);
    this.embla = null;
    this._onBreakpointChange = this._sync.bind(this);
  }

  start() {
    this.mediaQuery.addEventListener('change', this._onBreakpointChange);
    this._sync();
  }

  stop() {
    this.mediaQuery.removeEventListener('change', this._onBreakpointChange);
    this.destroy();
  }

  scrollTo(index, jump = true) {
    this.embla?.scrollTo(index, jump);
  }

  selectedIndex() {
    return this.embla?.selectedScrollSnap() ?? 0;
  }

  _sync() {
    if (this.mediaQuery.matches) this._init();
    else this.destroy();
  }

  _init() {
    if (this.embla || !this.viewport || !this.container) return;
    if (!window.EmblaCarousel) {
      window.setTimeout(() => this._init(), 50);
      return;
    }
    this.viewport.classList.add('embla');
    this.container.classList.add('embla__container');
    this.getSlides().forEach((slide) => slide.classList.add('embla__slide'));
    this.embla = window.EmblaCarousel(this.viewport, this.options);
    if (this.onSelect) {
      this.embla.on('select', () => this.onSelect(this._currentSlide()));
      this.onSelect(this._currentSlide());
    }
  }

  destroy() {
    if (!this.embla) return;
    this.embla.destroy();
    this.embla = null;
    this.viewport?.classList.remove('embla');
    this.container?.classList.remove('embla__container');
    this.getSlides().forEach((slide) => slide.classList.remove('embla__slide'));
  }

  _currentSlide() {
    return this.getSlides()[this.selectedIndex()] ?? null;
  }
}

// Product media stack: stacks images vertically on desktop and uses an Embla
// carousel with peek effect on mobile. The DOM contains only the currently
// selected variant slide plus the non-variant slides; on variant change we
// just rewrite the URL/srcset/id of the variant slide instead of re-rendering.
class ProductMediaStack extends HTMLElement {
  static MOBILE_BREAKPOINT = '(max-width: 999px)';

  connectedCallback() {
    this.viewport = this.querySelector('[data-product-media-viewport]');
    this.list = this.querySelector('[data-product-media-list]');
    this.zoomButton = this.querySelector('.product__zoom-button');
    this.formId = this.getAttribute('form-id');

    this.carousel = new ResponsiveEmbla({
      viewport: this.viewport,
      container: this.list,
      getSlides: () => this._getAllItems(),
      mediaQuery: ProductMediaStack.MOBILE_BREAKPOINT,
      onSelect: (slide) => slide && this._setSelectedItem(slide),
    });

    this._bindVariantChange();
    this._bindZoomTrigger();
    this.carousel.start();
  }

  disconnectedCallback() {
    this.carousel?.stop();
  }

  _bindVariantChange() {
    const form = this.formId ? document.getElementById(this.formId) : null;
    if (!form) return;
    form.addEventListener('variant:changed', this._onVariantChanged.bind(this));
  }

  _bindZoomTrigger() {
    if (!this.zoomButton || !this.list) return;
    this.list.addEventListener('click', (event) => {
      if (event.target.closest('button')) return;
      const item = event.target.closest('.product__media-item[data-media-type="image"]');
      if (!item) return;
      this._setSelectedItem(item);
      this.zoomButton.click();
    });
  }

  _onVariantChanged(event) {
    const variant = event.detail?.variant;
    const media = variant?.featured_media;
    if (!media) return;

    const slide = this.querySelector('[data-variant-slide]');
    if (!slide) return;
    slide.setAttribute('data-media-id', media.id);
    slide.id = `product-${this.id || ''}-${media.id}`;
    const img = slide.querySelector('img');
    if (img) ProductMediaStack._updateImage(img, media.preview_image);

    this.carousel.scrollTo(0, true);
  }

  // Rewrites img src/srcset to point at the new media, keeping the existing
  // widths so the browser can pick the right size from cache when possible.
  static _updateImage(img, previewImage) {
    if (!previewImage?.src) return;
    const baseSrc = previewImage.src.split('?')[0];
    const buildUrl = (width) => `${baseSrc}?width=${width}`;
    const widths = (img.getAttribute('srcset') || '')
      .split(',')
      .map((entry) => parseInt(entry.trim().split(' ')[1], 10))
      .filter(Boolean);
    img.src = buildUrl(img.width || previewImage.width);
    if (widths.length) {
      img.srcset = widths.map((w) => `${buildUrl(w)} ${w}w`).join(', ');
    }
    if (previewImage.width) img.width = previewImage.width;
    if (previewImage.height) img.height = previewImage.height;
  }

  _setSelectedItem(item) {
    this._getAllItems().forEach((node) => node.classList.toggle('is-selected', node === item));
    if (this.zoomButton) {
      this.zoomButton.hidden = item.getAttribute('data-media-type') !== 'image';
    }
  }

  _getAllItems() {
    return Array.from(this.list?.querySelectorAll('.product__media-item') ?? []);
  }
}

if (!window.customElements.get('product-media-stack')) {
  window.customElements.define('product-media-stack', ProductMediaStack);
}

// Product recommendations carousel: renders related products as a CSS grid on
// tablet/desktop, and switches to an Embla carousel with peek effect on mobile.
class ProductRecommendationsCarousel extends HTMLElement {
  static MOBILE_BREAKPOINT = '(max-width: 740px)';

  connectedCallback() {
    this.viewport = this.querySelector('[data-recommendations-viewport]');
    this.container = this.querySelector('[data-recommendations-list]');
    if (!this.viewport || !this.container) return;

    this.carousel = new ResponsiveEmbla({
      viewport: this.viewport,
      container: this.container,
      getSlides: () => Array.from(this.container.children),
      mediaQuery: ProductRecommendationsCarousel.MOBILE_BREAKPOINT,
    });
    this.carousel.start();
  }

  disconnectedCallback() {
    this.carousel?.stop();
  }
}

if (!window.customElements.get('product-recommendations-carousel')) {
  window.customElements.define('product-recommendations-carousel', ProductRecommendationsCarousel);
}

// Collapsible text: shows a teaser (first paragraph) and toggles the rest
// behind a "Lire la suite" / "Lire moins" button. The full content is always
// in the rendered HTML (with the native `hidden` attribute = display:none) so
// Googlebot crawls and indexes it normally.
class CollapsibleText extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.collection-description__toggle');
    this.more = this.querySelector('.collection-description__more');
    if (!this.toggle || !this.more) return;

    this.labelMore = this.toggle.dataset.labelMore || this.toggle.textContent.trim();
    this.labelLess = this.toggle.dataset.labelLess || this.labelMore;

    this.toggle.addEventListener('click', this._onToggle.bind(this));
  }

  _onToggle() {
    const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
    const next = !isExpanded;
    this.toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
    this.more.hidden = !next;
    this.toggle.textContent = next ? this.labelLess : this.labelMore;
  }
}

if (!window.customElements.get('collapsible-text')) {
  window.customElements.define('collapsible-text', CollapsibleText);
}